import React, { useEffect, useState } from "react";
import "../../../css/AddSubRoleModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


interface FormData {
  _id: string;
  name: string;
}

interface RoleData {
  key: string;
  _id: string;
  name: string;
  parent?: string | null;
}

interface AddSubRoleModalProps {
  onClose: () => void;
  parentRoles: { _id: string; name: string }[];
  onSubRoleAdded?: () => void;
}

const AddSubRoleModal: React.FC<AddSubRoleModalProps> = ({
  onClose,
  onSubRoleAdded,
}) => {
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [forms, setForms] = useState<FormData[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [subRoleName, setSubRoleName] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  // const fetchRoles = async () => {
  //   try {
  //     const token = sessionStorage.getItem("accessToken");
  //     const { data } = await api.auth_api.get<RoleData[]>("/roles", {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });

  //     const topLevelRoles = data.filter((role) => role.parent === null);
  //     setRoles(topLevelRoles);
  //   } catch (err) {
  //     console.error("Error fetching roles:", err);
  //   }
  // };

  const fetchRoles = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      const { data } = await api.auth_api.get<RoleData[]>("/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const EXCLUDED_ROLE_KEYS = ["SUPER ADMIN", "CENTER ADMIN"];

      const filteredRoles = data.filter(
        (role) =>
          role.parent === null && // only parent roles
          !EXCLUDED_ROLE_KEYS.includes(role.key)
      );

      setRoles(filteredRoles);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };



  // ✅ Fetch forms for selected role
  const fetchRoleForms = async (roleId: string) => {
    if (!roleId) return;

    try {
      const token = sessionStorage.getItem("accessToken");
      const { data } = await api.auth_api.get(
        `/roles/${roleId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // ✅ Extract formIds array from response
      setForms(data.formIds || []);
      setSelectedForms([]);
    } catch (err) {
      console.error(`Error fetching forms for role ${roleId}:`, err);
      setForms([]);
    }
  };

  const handleCheckboxChange = (formId: string) => {
    setSelectedForms((prev) =>
      prev.includes(formId)
        ? prev.filter((id) => id !== formId)
        : [...prev, formId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedRoleId || !subRoleName.trim()) {
      showToast("Please select a role and enter sub-role name", 'error');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");

      await api.auth_api.post(
        "/roles",
        {
          name: subRoleName,
          key: subRoleName.toUpperCase(),
          details,
          parentId: selectedRoleId,
          formIds: selectedForms,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      showToast("Sub-role created successfully ✅", 'success');

      if (onSubRoleAdded) {
        onSubRoleAdded();
      }

      onClose();
    } catch (error) {
      console.error("Error adding sub-role:", error);
      showToast("Failed to add sub-role ❌", 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="subrole-modal-overlay" onClick={onClose}>
      <div className="subrole-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="subrole-close-btn" onClick={onClose}>
          ✕
        </button>
        <h2 className="subrole-title">Add New Sub-Role</h2>

        {/* Choose Role */}
        <label className="subrole-label">Choose Role</label>
        <select
          className="subrole-input"
          value={selectedRoleId}
          onChange={(e) => {
            const roleId = e.target.value;
            setSelectedRoleId(roleId);
            fetchRoleForms(roleId); // ✅ fetch forms when role changes
          }}
        >
          <option value="">Select Role</option>
          {roles.map((role) => (
            <option key={role._id} value={role._id}>
              {role.name}
            </option>
          ))}
        </select>

        {/* Sub Role Name */}
        <label className="subrole-label">Sub-Role Name</label>
        <input
          type="text"
          placeholder="Enter sub-role name"
          className="subrole-input"
          value={subRoleName}
          onChange={(e) => setSubRoleName(e.target.value)}
        />

        {/* Details */}
        <label className="subrole-label">Details</label>
        <textarea
          placeholder="Enter details about this sub-role"
          className="subrole-input"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />

        {/* Assign Forms */}
        <label className="subrole-label">Assign Forms</label>
        <div className="subrole-forms">
          {forms.length > 0 ? (
            forms.map((form) => (
              <label
                key={form._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",          // 👈 space between checkbox & text
                  marginBottom: "6px"  // 👈 space between rows
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedForms.includes(form._id)}
                  onChange={() => handleCheckboxChange(form._id)}
                />
                {form.name}
              </label>

            ))
          ) : (
            <p className="empty-text">
              {selectedRoleId
                ? "No forms available for this role."
                : "Please select a Role to see forms."}
            </p>
          )}
        </div>

        <button
          className="subrole-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Adding..." : "Add Sub-Role"}
        </button>
      </div>
    </div>
  );
};

export default AddSubRoleModal;
